// https://github.com/magic-akari/seamless-scroll-polyfill

enum WritingMode {
  HorizontalTb,
  VerticalRl,
  VerticalLr,
  SidewaysRl,
  SidewaysLr,
}

enum ScrollAlignment {
  ToEdgeIfNeeded,
  CenterAlways,
  LeftOrTop,
  RightOrBottom,
}

interface IScrollConfig {
  readonly duration?: number;
  readonly timingFunc?: (k: number) => number;
}

interface ScrollMethod<T extends Element | typeof window> {
  (target: T, scrollOptions?: ScrollToOptions, config?: IScrollConfig): void;
}

interface BackupMethod {
  <K extends keyof Element>(proto: Element, method: K): Element[K] | undefined;
  <K extends keyof Element>(proto: Element, method: K, fallback: unknown): Element[K];
  <K extends keyof Window>(proto: Window, method: K): Window[K] | undefined;
  <K extends keyof Window>(proto: Window, method: K, fallback: unknown): Window[K];
}

interface IContext extends IScrollConfig {
  readonly timeStamp: number;
  readonly startX: number;
  readonly startY: number;
  readonly targetX: number;
  readonly targetY: number;
  readonly method: (x: number, y: number) => void;
  readonly callback: () => void;
  rafId: number;
}

type Tuple2<T> = [T, T];

const isObject = (value: unknown): boolean => {
  const type = typeof value;
  return value !== null && (type === 'object' || type === 'function');
};
const isWindow = (obj: unknown): obj is Window => (obj as Window).window === obj;
const checkBehavior = (behavior?: string): behavior is undefined | ScrollBehavior => {
  return (
    behavior === undefined || behavior === 'auto' || behavior === 'instant' || behavior === 'smooth'
  );
};
const failedExecute = (
  method: string,
  object: string,
  reason = 'cannot convert to dictionary.'
): string => `Failed to execute '${method}' on '${object}': ${reason}`;

// https://drafts.csswg.org/cssom-view/#normalize-non-finite-values
const nonFinite = (value: unknown): number => {
  if (!Number.isFinite(value as number)) {
    return 0;
  }
  return Number(value);
};

const backupMethod: BackupMethod = (proto: any, method: string, fallback?: unknown) => {
  const backup = `__SEAMLESS.BACKUP$${method}`;

  if (!proto[backup] && proto[method] && !proto[method]['__isPolyfill']) {
    proto[backup] = proto[method];
  }

  return proto[backup] || fallback;
};

const isConnected = (node: Node) => {
  return (
    node.isConnected ??
    (!node.ownerDocument ||
      // eslint-disable-next-line no-bitwise
      !(node.ownerDocument.compareDocumentPosition(node) & /** DOCUMENT_POSITION_DISCONNECTED */ 1))
  );
};

function elementScrollXY(this: Element, x: number, y: number): void {
  this.scrollLeft = x;
  this.scrollTop = y;
}

const scrollWithOptions = (
  element: Element,
  options: Readonly<ScrollToOptions>,
  config?: IScrollConfig
): void => {
  if (!isConnected(element)) {
    return;
  }

  const startX = element.scrollLeft;
  const startY = element.scrollTop;

  const targetX = nonFinite(options.left ?? startX);
  const targetY = nonFinite(options.top ?? startY);

  if (targetX === startX && targetY === startY) {
    return;
  }

  const fallback = backupMethod(HTMLElement.prototype, 'scroll', elementScrollXY);
  const method = backupMethod(Object.getPrototypeOf(element) as Element, 'scroll', fallback).bind(
    element
  );

  if (options.behavior !== 'smooth') {
    method(targetX, targetY);
    return;
  }

  const removeEventListener = () => {
    window.removeEventListener('wheel', cancelScroll);
    window.removeEventListener('touchmove', cancelScroll);
  };

  const step = (context: IContext): void => {
    const DURATION = 500;
    const ease = (k: number) => {
      return 0.5 * (1 - Math.cos(Math.PI * k));
    };

    const currentTime = window.Date.now();

    const elapsed = (currentTime - context.timeStamp) / (context.duration || DURATION);
    if (elapsed > 1) {
      context.method(context.targetX, context.targetY);
      context.callback();
      return;
    }
    const value = (context.timingFunc || ease)(elapsed);

    const currentX = context.startX + (context.targetX - context.startX) * value;
    const currentY = context.startY + (context.targetY - context.startY) * value;

    context.method(currentX, currentY);

    context.rafId = window.requestAnimationFrame(() => {
      step(context);
    });
  };

  const context: IContext = {
    ...config,
    timeStamp: window.Date.now(),
    startX,
    startY,
    targetX,
    targetY,
    rafId: 0,
    method,
    callback: removeEventListener,
  };

  const cancelScroll = () => {
    window.cancelAnimationFrame(context.rafId);
    removeEventListener();
  };

  window.addEventListener('wheel', cancelScroll, {
    passive: true,
    once: true,
  });
  window.addEventListener('touchmove', cancelScroll, {
    passive: true,
    once: true,
  });

  step(context);
};

const createScroll =
  <T extends Element | typeof window>(
    scrollName: 'scroll' | 'scrollTo' | 'scrollBy'
  ): ScrollMethod<T> =>
  (target, scrollOptions, config): void => {
    const [element, scrollType]: [Element, 'Window' | 'Element'] = isWindow(target)
      ? [scrollingElement(target.document.documentElement), 'Window']
      : [target, 'Element'];

    const options = scrollOptions ?? {};

    if (!isObject(options)) {
      throw new TypeError(failedExecute(scrollName, scrollType));
    }

    if (!checkBehavior(options.behavior)) {
      throw new TypeError(failedExecute(scrollName, scrollType, options.behavior));
    }

    if (scrollName === 'scrollBy') {
      options.left = nonFinite(options.left) + element.scrollLeft;
      options.top = nonFinite(options.top) + element.scrollTop;
    }

    scrollWithOptions(element, options, config);
  };

const scroll = createScroll('scroll');
const elementScroll = scroll as ScrollMethod<Element>;

/**
 * - On Chrome and Firefox, document.scrollingElement will return the <html> element.
 * - Safari, document.scrollingElement will return the <body> element.
 * - On Edge, document.scrollingElement will return the <body> element.
 * - IE11 does not support document.scrollingElement, but you can assume its <html>.
 */
const scrollingElement = (element: Element): Element =>
  element.ownerDocument.scrollingElement || element.ownerDocument.documentElement;

// https://drafts.csswg.org/css-writing-modes-4/#block-flow
const normalizeWritingMode = (writingMode: string): WritingMode => {
  switch (writingMode) {
    case 'horizontal-tb':
    case 'lr':
    case 'lr-tb':
    case 'rl':
    case 'rl-tb':
      return WritingMode.HorizontalTb;
    case 'vertical-rl':
    case 'tb':
    case 'tb-rl':
      return WritingMode.VerticalRl;
    case 'vertical-lr':
    case 'tb-lr':
      return WritingMode.VerticalLr;
    case 'sideways-rl':
      return WritingMode.SidewaysRl;
    case 'sideways-lr':
      return WritingMode.SidewaysLr;

    default:
      return WritingMode.HorizontalTb;
  }
};

const calcPhysicalAxis = <T>(
  writingMode: WritingMode,
  isLTR: boolean,
  hPos: T,
  vPos: T
): [number, T, T] => {
  let layout = 0b00;

  enum OP {
    ReverseHorizontal = 0b01,
    ReverseVertical = 0b10,
  }

  if (!isLTR) {
    layout ^= OP.ReverseVertical;
  }

  switch (writingMode) {
    case WritingMode.HorizontalTb:
      // swap horizontal and vertical
      layout = (layout >> 1) | ((layout & 1) << 1);
      [hPos, vPos] = [vPos, hPos];
      break;

    case WritingMode.VerticalRl:
    case WritingMode.SidewaysRl:
      //  reverse horizontal
      layout ^= OP.ReverseHorizontal;
      break;

    case WritingMode.SidewaysLr:
      // reverse vertical
      layout ^= OP.ReverseVertical;
      break;

    default:
      break;
  }

  return [layout, hPos, vPos];
};

const toPhysicalAlignment = (
  options: Readonly<ScrollIntoViewOptions>,
  writingMode: WritingMode,
  isLTR: boolean
): Tuple2<ScrollAlignment> => {
  const [layout, hPos, vPos] = calcPhysicalAxis(
    writingMode,
    isLTR,
    options.block || 'start',
    options.inline || 'nearest'
  );

  return [hPos, vPos].map((value, index) => {
    switch (value) {
      case 'center':
        return ScrollAlignment.CenterAlways;
      case 'nearest':
        return ScrollAlignment.ToEdgeIfNeeded;

      default: {
        const reverse = (layout >> index) & 1;
        return (value === 'start') === !reverse
          ? ScrollAlignment.LeftOrTop
          : ScrollAlignment.RightOrBottom;
      }
    }
  }) as Tuple2<ScrollAlignment>;
};

const getSupportedScrollMarginProperty = (
  ownerDocument: Document
): 'scroll-margin' | 'scroll-snap-margin' | undefined => {
  // Webkit uses "scroll-snap-margin" https://bugs.webkit.org/show_bug.cgi?id=189265.
  return (['scroll-margin', 'scroll-snap-margin'] as const).filter(
    // eslint-disable-next-line no-restricted-syntax
    (property) => property in ownerDocument.documentElement.style
  )[0];
};

const getElementScrollSnapArea = (
  element: Element,
  elementRect: Readonly<DOMRect>,
  computedStyle: Readonly<CSSStyleDeclaration>
): [top: number, right: number, bottom: number, left: number] => {
  const { top, right, bottom, left } = elementRect;
  const scrollProperty = getSupportedScrollMarginProperty(element.ownerDocument);
  if (!scrollProperty) {
    return [top, right, bottom, left];
  }

  const scrollMarginValue = (edge: 'top' | 'right' | 'bottom' | 'left'): number => {
    const value = computedStyle.getPropertyValue(`${scrollProperty}-${edge}`);
    return parseInt(value, 10) || 0;
  };

  return [
    top - scrollMarginValue('top'),
    right + scrollMarginValue('right'),
    bottom + scrollMarginValue('bottom'),
    left - scrollMarginValue('left'),
  ];
};

const getFrameElement = (element: Element): Element | null => {
  try {
    return element.ownerDocument.defaultView?.frameElement || null;
  } catch {
    return null;
  }
};

const parentElement = (element: Element): Element | null => {
  const pNode = element.parentNode;
  const pElement = element.parentElement;

  if (pElement === null && pNode !== null) {
    if (pNode.nodeType === /** Node.DOCUMENT_FRAGMENT_NODE */ 11) {
      return (pNode as ShadowRoot).host;
    }
    if (pNode.nodeType === /** Node.DOCUMENT_NODE */ 9) {
      return getFrameElement(element);
    }
  }

  return pElement;
};

const canOverflow = (overflow: string | null): boolean => {
  return overflow !== 'visible' && overflow !== 'clip';
};

const isScrollable = (element: Element, computedStyle: Readonly<CSSStyleDeclaration>): boolean => {
  if (element.clientHeight < element.scrollHeight || element.clientWidth < element.scrollWidth) {
    return (
      canOverflow(computedStyle.overflowY) ||
      canOverflow(computedStyle.overflowX) ||
      element === scrollingElement(element)
    );
  }

  return false;
};

const getFrameViewport = (frame: Element, frameRect: Readonly<DOMRect>) => {
  const visualViewport = frame.ownerDocument.defaultView?.visualViewport;
  const [x, y, width, height] =
    frame === scrollingElement(frame)
      ? [
          0,
          0,
          visualViewport?.width ?? frame.clientWidth,
          visualViewport?.height ?? frame.clientHeight,
        ]
      : [frameRect.left, frameRect.top, frame.clientWidth, frame.clientHeight];

  const left = x + frame.clientLeft;
  const top = y + frame.clientTop;
  const right = left + width;
  const bottom = top + height;

  return [top, right, bottom, left] as const;
};

const mapNearest = (
  align: ScrollAlignment,
  scrollingEdgeStart: number,
  scrollingEdgeEnd: number,
  scrollingSize: number,
  elementEdgeStart: number,
  elementEdgeEnd: number,
  elementSize: number
): Exclude<ScrollAlignment, ScrollAlignment.ToEdgeIfNeeded> | null => {
  if (align !== ScrollAlignment.ToEdgeIfNeeded) {
    return align;
  }

  if (
    (elementEdgeStart < scrollingEdgeStart && elementEdgeEnd > scrollingEdgeEnd) ||
    (elementEdgeStart > scrollingEdgeStart && elementEdgeEnd < scrollingEdgeEnd)
  ) {
    return null;
  }

  if (
    (elementEdgeStart <= scrollingEdgeStart && elementSize <= scrollingSize) ||
    (elementEdgeEnd >= scrollingEdgeEnd && elementSize >= scrollingSize)
  ) {
    return ScrollAlignment.LeftOrTop;
  }

  if (
    (elementEdgeEnd > scrollingEdgeEnd && elementSize < scrollingSize) ||
    (elementEdgeStart < scrollingEdgeStart && elementSize > scrollingSize)
  ) {
    return ScrollAlignment.RightOrBottom;
  }

  return null;
};

const calcAlignEdge = (align: ScrollAlignment, start: number, end: number): number => {
  switch (align) {
    case ScrollAlignment.CenterAlways:
      return (start + end) / 2;

    case ScrollAlignment.RightOrBottom:
      return end;

    case ScrollAlignment.LeftOrTop:
    case ScrollAlignment.ToEdgeIfNeeded:
      return start;
    default:
      return null;
  }
};

const isXReversed = (computedStyle: Readonly<CSSStyleDeclaration>): boolean => {
  const layout = calcPhysicalAxis(
    normalizeWritingMode(computedStyle.writingMode),
    computedStyle.direction !== 'rtl',
    undefined,
    undefined
  )[0];
  return (layout & 1) === 1;
};

const clamp = (value: number, min: number, max: number): number => {
  if (value < min) {
    return min;
  }

  if (value > max) {
    return max;
  }

  return value;
};

const computeScrollIntoView = (
  element: Element,
  options: ScrollIntoViewOptions
): [Element, ScrollToOptions][] => {
  // Collect all the scrolling boxes, as defined in the spec: https://drafts.csswg.org/cssom-view/#scrolling-box
  const actions: [Element, ScrollToOptions][] = [];

  let { ownerDocument } = element;
  let ownerWindow = ownerDocument['defaultView'];

  if (!ownerWindow) return actions;

  const computedStyle = window.getComputedStyle(element);
  const isLTR = computedStyle.direction !== 'rtl';

  const writingMode = normalizeWritingMode(
    computedStyle.writingMode ||
      computedStyle.getPropertyValue('-webkit-writing-mode') ||
      computedStyle.getPropertyValue('-ms-writing-mode')
  );

  const [alignH, alignV] = toPhysicalAlignment(options, writingMode, isLTR);

  let [top, right, bottom, left] = getElementScrollSnapArea(
    element,
    element.getBoundingClientRect(),
    computedStyle
  );

  for (let frame = parentElement(element); frame !== null; frame = parentElement(frame)) {
    if (ownerDocument !== frame.ownerDocument) {
      ownerDocument = frame.ownerDocument;
      ownerWindow = ownerDocument.defaultView;
      if (!ownerWindow) break;

      const { left: dX, top: dY } = frame.getBoundingClientRect();
      top += dY;
      right += dX;
      bottom += dY;
      left += dX;
    }

    const frameStyle = ownerWindow.getComputedStyle(frame);

    if (frameStyle.position === 'fixed') break;
    if (!isScrollable(frame, frameStyle)) continue;

    const frameRect = frame.getBoundingClientRect();

    const [frameTop, frameRight, frameBottom, frameLeft] = getFrameViewport(frame, frameRect);

    const eAlignH = mapNearest(
      alignH,
      frameLeft,
      frameRight,
      frame.clientWidth,
      left,
      right,
      right - left
    );

    const eAlignV = mapNearest(
      alignV,
      frameTop,
      frameBottom,
      frame.clientHeight,
      top,
      bottom,
      bottom - top
    );

    const diffX =
      eAlignH === null
        ? 0
        : calcAlignEdge(eAlignH, left, right) - calcAlignEdge(eAlignH, frameLeft, frameRight);
    const diffY =
      eAlignV === null
        ? 0
        : calcAlignEdge(eAlignV, top, bottom) - calcAlignEdge(eAlignV, frameTop, frameBottom);

    const moveX = isXReversed(frameStyle)
      ? clamp(diffX, -frame.scrollWidth + frame.clientWidth - frame.scrollLeft, -frame.scrollLeft)
      : clamp(diffX, -frame.scrollLeft, frame.scrollWidth - frame.clientWidth - frame.scrollLeft);

    const moveY = clamp(
      diffY,
      -frame.scrollTop,
      frame.scrollHeight - frame.clientHeight - frame.scrollTop
    );

    actions.push([
      frame,
      { left: frame.scrollLeft + moveX, top: frame.scrollTop + moveY, behavior: options.behavior },
    ]);

    top = Math.max(top - moveY, frameTop);
    right = Math.min(right - moveX, frameRight);
    bottom = Math.min(bottom - moveY, frameBottom);
    left = Math.max(left - moveX, frameLeft);
  }

  return actions;
};

export const scrollIntoView = (
  element: Element,
  scrollIntoViewOptions?: ScrollIntoViewOptions,
  config?: IScrollConfig
): void => {
  const options = scrollIntoViewOptions || {};

  if (!checkBehavior(options.behavior)) {
    throw new TypeError(failedExecute('scrollIntoView', 'Element', options.behavior));
  }

  const actions = computeScrollIntoView(element, options);

  actions.forEach(([frame, scrollToOptions]) => {
    elementScroll(frame, scrollToOptions, config);
  });
};
